import { useState, forwardRef } from "react";
import classNames from "classnames";

import { scrollElementIntoView } from "@lib/helpers";
import Price from "@components/Price";
import AddToCart from "@containers/Catalog/Actions/AddToCart";
import ArrowDown from "@components/Icons/ArrowDown";
import BundleItem from "@containers/Catalog/Bundles/BundleItem";

const INITIAL_MAX_VISIBLE_ITEMS = 1;

const Bundles = forwardRef(({ productBundles }, ref) => {
  const [maxVisibleListItems, setMaxVisibleListItems] = useState(
    INITIAL_MAX_VISIBLE_ITEMS
  );

  const { bundles, product } = productBundles;
  const bundlesCount = bundles.length;

  const expandList = (event) => {
    event.preventDefault();

    setMaxVisibleListItems((previous) => {
      // Show 5
      if (previous === 2) {
        return bundlesCount > 5 ? 5 : bundlesCount;
      }
      // Show 10
      if (previous === 5) {
        return bundlesCount > 10 ? 10 : bundlesCount;
      }
      // Show all
      return bundlesCount;
    });
  };

  const collapseList = (event) => {
    event.preventDefault();
    setMaxVisibleListItems(INITIAL_MAX_VISIBLE_ITEMS);

    setTimeout(() => {
      scrollElementIntoView(ref.current, "smooth");
    }, 50);
  };

  return (
    <>
      <div className="mt-5 mb-6 package-wrapper">
        {bundles
          .filter((visibleBundle, index) => maxVisibleListItems >= index + 1)
          .map((bundle, bundleIndex) => {
            const bundleProducts = [product, ...bundle.products];
            const bundleProductsCount = bundleProducts.length;

            return (
              <div
                className="p-[15px] lg:p-[20px]"
                key={bundleIndex}
                id={`pachet-${bundleIndex + 1}`}
              >
                <div className="flex flex-col md:flex-row items-center justify-center">
                  <>
                    <div
                      className={classNames(
                        "flex flex-col md:flex-row relative",
                        {
                          "md:flex-wrap": bundleProductsCount > 2,
                          "md:justify-between": bundleProductsCount < 3,
                        }
                      )}
                    >
                      {bundleProducts.map(
                        (bundleProduct, bundleProductIndex) => (
                          <div
                            key={bundleProductIndex}
                            className={classNames(
                              "w-full md:space-x-[8px] md:p-[8px]",
                              {
                                "md:max-w-[50%]": bundleProductsCount > 2,
                                "mt-[12px] md:mt-0": bundleProductIndex > 0,
                              }
                            )}
                          >
                            <BundleItem
                              key={`package-${bundleIndex}-product-${bundleProductIndex}`}
                              bundleProductsCount={bundleProductsCount}
                              bundleProductIndex={bundleProductIndex}
                              bundleProduct={bundleProduct}
                            />
                          </div>
                        )
                      )}

                      {/* desktop view separator start */}
                      <div
                        aria-hidden="true"
                        className={classNames(
                          "hidden md:block absolute text-[44px] -right-[10px] z-[1] top-1/2 -translate-y-1/2 select-none text-yellow",
                          {
                            "top-[80%]": bundleProductsCount > 2,
                          }
                        )}
                      >
                        =
                      </div>
                    </div>
                    {/* desktop view separator end */}

                    <div
                      className={classNames(
                        "flex gap-[16px] w-full py-4 justify-end md:justify-center items-center md:flex-col md:pl-[16px]",
                        {
                          "md:w-[50%] self-end": bundleProductsCount > 2,
                          "md:w-[30%]": bundleProductsCount === 2,
                        }
                      )}
                    >
                      <Price
                        price={bundle.price}
                        regularPrice={bundle.regular_price}
                        formatted={true}
                        displayDiscount={true}
                        isBundle={true}
                        size="navbar"
                      />

                      <AddToCart
                        iconOnlyOnMobile
                        bundleProducts={bundleProducts}
                        product={product}
                        isBundle={true}
                        noMargin={true}
                      />
                    </div>
                  </>
                </div>
              </div>
            );
          })}
      </div>

      <div className="flex justify-center mt-2 md:mt-0 pt-1">
        {bundlesCount > maxVisibleListItems && (
          <button
            type="button"
            aria-expanded={maxVisibleListItems < bundlesCount}
            className="flex items-center text-red-brand"
            onClick={expandList}
          >
            Vezi mai multe pachete
            <div className="ml-2">
              <ArrowDown />
            </div>
          </button>
        )}

        {maxVisibleListItems === bundlesCount &&
          bundlesCount > INITIAL_MAX_VISIBLE_ITEMS && (
            <button
              type="button"
              aria-expanded={false}
              className="flex items-center text-red-brand"
              onClick={collapseList}
            >
              Restrange lista{" "}
              <div className="rotate-180 ml-2">
                <ArrowDown />
              </div>
            </button>
          )}
      </div>
    </>
  );
});

Bundles.displayName = "Bundles";

export default Bundles;
